import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import apiService from 'services/api'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { Switch } from '../../../components/UI/Switch/Switch'
import { Select } from '../../../components/UI/Select/Select'
import { MISC_SETTINGS } from '../../../models/misc_settings'
import { MiscSetting } from '../../../models/preferences'
import { queryClient } from '../../../services/queryClient'
import { notifySuccess, notifyError } from '../../../services/toast'
import { REPORTING_UNITS } from '../../../constants/reportingUnits'
import * as S from './styles'

const description = `Customize your First Bite experience with the following preferences.`

export function MiscSettingsTable() {
  const api = apiService()
  const mutation = api.useChangeMiscSettings()
  const updateReportingUnitMutation = api.useUpdateReportingUnit()
  const { data, isLoading } = api.useGetPreferences()

  const [isApplyingTempClosed, setIsApplyingTempClosed] = useState(false)

  async function handleChangeTempClosed(val: boolean) {
    setIsApplyingTempClosed(true)
    await mutation.mutateAsync({
      key: 'show_temp_closed',
      value: val ? '1' : '0',
    })
    setIsApplyingTempClosed(false)
    notifySuccess('Setting Changed Successfully')
    queryClient.clear()
  }

  async function handleUpdateReportingUnit(newValue: string) {
    const currentValue = data?.reporting_unit || ''

    // Don't save if value hasn't changed
    if (newValue === currentValue) {
      return
    }

    if (!newValue) {
      notifyError('Base unit cannot be blank')
      return
    }

    try {
      await updateReportingUnitMutation.mutateAsync({
        reporting_unit: newValue,
      })
      notifySuccess('Base Unit Updated Successfully')
      queryClient.invalidateQueries({ queryKey: ['preferences'] })
    } catch (error) {
      notifyError('Failed to update base unit')
    }
  }

  const columns = useMemo<ColumnDef<MiscSetting, any>[]>(
    () => [
      {
        accessorKey: 'key',
        header: 'Setting',
        cell: (info) =>
          MISC_SETTINGS[info.getValue() as keyof typeof MISC_SETTINGS].title,
        size: 150,
      },
      {
        id: 'description',
        accessorKey: 'key',
        header: 'Description',
        cell: (info) =>
          MISC_SETTINGS[info.getValue() as keyof typeof MISC_SETTINGS]
            .description,
        size: 300,
      },
      {
        id: 'edit',
        header: 'Actions',
        size: 70,
        meta: { rightAlign: false },
        cell: ({ row }) => {
          if (row.original.key === 'show_temp_closed') {
            if (isApplyingTempClosed) {
              return <Spinner animation="border" />
            }
            return (
              <div className={'justify-end flex'}>
                <Switch
                  onChange={(e) => handleChangeTempClosed(e.target.checked)}
                  defaultChecked={!!parseInt(row.original.value)}
                />
              </div>
            )
          }
          if (row.original.key === 'reporting_unit') {
            return (
              <div className={'justify-end flex gap-2 items-center'}>
                {updateReportingUnitMutation.isPending ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Select
                    items={REPORTING_UNITS.map((unit) => ({
                      label: `${unit.singular} / ${unit.plural}`,
                      value: unit.value,
                    }))}
                    selected={row.original.value}
                    onSelect={(newValue) => {
                      handleUpdateReportingUnit(newValue as string)
                    }}
                    buttonSize="sm"
                    buttonVariant="outline"
                    buttonClassName="w-[150px]"
                    matchTriggerWidth
                    searchPlaceholder="Search units..."
                  />
                )}
              </div>
            )
          }
          return null
        },
      },
    ],
    [data, isApplyingTempClosed, updateReportingUnitMutation.isPending]
  )

  // hotfix: exclude items which do not have a description in MISC_SETTINGS
  // Also add reporting_unit as a pseudo-MiscSetting since it's stored on Company model
  const filteredData = useMemo(() => {
    const miscSettings = (data?.misc_settings || []).filter(
      (setting) => MISC_SETTINGS[setting.key as keyof typeof MISC_SETTINGS]
    )

    // Add reporting_unit as a pseudo-setting
    if (data?.reporting_unit) {
      miscSettings.push({
        key: 'reporting_unit' as any,
        value: data.reporting_unit,
      })
    }

    return miscSettings
  }, [data])

  if (isLoading || !data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <DataTableContainer>
      <ClientDataTable
        height={'100%'}
        tableHeader={
          <S.SettingsTableHeader>
            <S.TableDescriptionText>
              <OverflownTextTooltip
                tooltipText={description}
                maxLines={4}
                placement="bottom"
              >
                {description}
              </OverflownTextTooltip>
            </S.TableDescriptionText>
          </S.SettingsTableHeader>
        }
        stickyLastColumn={true}
        data={filteredData}
        columns={columns}
      />
    </DataTableContainer>
  )
}
